import type {
  ApplicationDetail,
  ApplicationFastenerLine,
} from "@/api/applications";

/** POC computeAggregates + syncDerivedCompareInputs */
export type PortfolioAggregates = {
  totalAnnualVolume: number;
  totalFastenersPerYear: number;
  avgFastenersPerPart: number;
  avgElementTypesPerPart: number;
  partsPerYear: number;
};

function fastenerQtyTotal(f: ApplicationFastenerLine): number {
  let a =
    f.qtyPerPartA != null && Number.isFinite(f.qtyPerPartA) ? f.qtyPerPartA : NaN;
  let b =
    f.qtyPerPartB != null && Number.isFinite(f.qtyPerPartB) ? f.qtyPerPartB : NaN;
  const q =
    f.qtyPerPart != null && Number.isFinite(f.qtyPerPart) ? f.qtyPerPart : NaN;
  if (!Number.isFinite(a) && Number.isFinite(q) && !Number.isFinite(b)) {
    a = q;
  }
  if (!Number.isFinite(a)) a = 0;
  if (!Number.isFinite(b)) b = 0;
  return a + b;
}

export function computePortfolioAggregates(
  apps: ApplicationDetail[],
): PortfolioAggregates | null {
  if (apps.length === 0) return null;

  let totalAnnualVolume = 0;
  let totalFastenersPerYear = 0;
  let weightedTypes = 0;

  for (const app of apps) {
    const vol =
      app.annualVolume != null && Number.isFinite(app.annualVolume)
        ? app.annualVolume
        : NaN;
    if (!Number.isFinite(vol)) continue;

    totalAnnualVolume += vol;

    const fastenersPerPart = (app.fasteners ?? []).reduce((sum, f) => {
      const q = fastenerQtyTotal(f);
      return sum + (Number.isFinite(q) ? q : 0);
    }, 0);

    totalFastenersPerYear += vol * fastenersPerPart;

    const typesWithQty = (app.fasteners ?? []).filter((f) => {
      const q = fastenerQtyTotal(f);
      return Number.isFinite(q) && q > 0;
    }).length;
    weightedTypes += vol * typesWithQty;
  }

  if (totalAnnualVolume <= 0) return null;

  return {
    totalAnnualVolume,
    totalFastenersPerYear,
    avgFastenersPerPart: totalFastenersPerYear / totalAnnualVolume,
    avgElementTypesPerPart: weightedTypes / totalAnnualVolume,
    partsPerYear: totalAnnualVolume,
  };
}

export function formatPortfolioInteger(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

export function formatPortfolioDecimal(n: number, decimals: number): string {
  const p = 10 ** decimals;
  const rounded = Math.round(n * p) / p;
  return rounded.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

export function formatAggregateMetric(
  value: number | null | undefined,
  decimals = 0,
): string {
  if (value == null || !Number.isFinite(value) || value <= 0) return "—";
  return decimals === 0
    ? formatPortfolioInteger(value)
    : formatPortfolioDecimal(value, decimals);
}
